import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import type { SubmitResult } from "../client";
import type { WherdState } from "../store";
import { resolveTheme, type ResolvedTheme } from "../theme";
import type { ActivationConfig, Announcement, ChangelogEntry, FeatureRequest, MessageThread, MessageType, WherdUser, WherdView } from "../types";
import { useWherdClient } from "./context";

// Subscribes to the observable store. Re-renders on any state change.
export function useWherdState(): WherdState {
  const client = useWherdClient();
  return useSyncExternalStore(
    client.store.subscribe,
    client.store.getSnapshot,
    client.store.getSnapshot,
  );
}

// Imperative API + reactive state — the main hook for custom UIs.
export function useWherd() {
  const client = useWherdClient();
  const state = useWherdState();

  return {
    ...state,
    open: useCallback((view?: WherdView) => client.open(view), [client]),
    close: useCallback(() => client.close(), [client]),
    setView: useCallback((view: WherdView) => client.setView(view), [client]),
    identify: useCallback((user: WherdUser) => client.identify(user), [client]),
    start: useCallback(
      (input: { body: string; metadata?: Record<string, unknown> }) =>
        client.startMessage(input),
      [client],
    ),
  };
}

// Headless composer for starting a new message thread — manages draft/submit/
// error state. The prebuilt widget is built on this, so custom UIs have parity.
export function useComposer() {
  const client = useWherdClient();
  const [body, setBody] = useState("");
  const [type, setType] = useState<MessageType>("idea");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [queued, setQueued] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(async (): Promise<SubmitResult | undefined> => {
    if (!body.trim()) {
      setError("Please enter a message");
      return;
    }
    setSubmitting(true);
    setError(null);
    const res = await client.startMessage({
      body: body.trim(),
      metadata: {
        type,
        ...(email.trim() ? { email: email.trim() } : {}),
      },
    });
    setSubmitting(false);
    if (res.ok) {
      setSubmitted(true);
      setQueued(res.queued);
      setBody("");
    } else {
      setError(res.error ?? "Something went wrong");
    }
    return res;
  }, [client, body, type, email]);

  const reset = useCallback(() => {
    setSubmitted(false);
    setQueued(false);
    setError(null);
    setBody("");
  }, []);

  return { body, setBody, type, setType, email, setEmail, submit, submitting, submitted, queued, error, reset };
}

// The end-user's own message threads (with replies + unread admin replies).
export function useMessages() {
  const client = useWherdClient();
  const state = useWherdState();
  // Threads are store-backed so every refresh (a reply here, an SSE event, or a
  // panel re-open elsewhere) updates one source and re-renders all views live.
  const threads = state.messages;
  const unreadTotal = state.messagesUnread;
  const isOpen = state.isOpen;
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    await client.refreshMessages();
    setLoading(false);
  }, [client]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // The widget mounts once and stays mounted, so re-opening the panel wouldn't
  // otherwise refetch. Pull fresh threads on each open→true edge so admin
  // replies (and the unread badge) are current whenever the panel is shown.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpen.current) void refresh();
    wasOpen.current = isOpen;
  }, [isOpen, refresh]);

  const reply = useCallback(
    async (id: string, body: string) => {
      const ok = await client.replyToMessage(id, body);
      if (ok) await refresh();
      return ok;
    },
    [client, refresh],
  );

  const markRead = useCallback(
    async (id: string) => {
      await client.markMessageRead(id);
      await refresh();
    },
    [client, refresh],
  );

  return { threads, unreadTotal, loading, refresh, reply, markRead };
}

// Cheap reactive unread-replies count (for a badge).
export function useUnreadMessages(): number {
  return useWherdState().messagesUnread;
}

// Active announcement banners + dismissal.
export function useAnnouncements() {
  const client = useWherdClient();
  const announcements: Announcement[] = useWherdState().announcements;
  const dismiss = useCallback(
    (id: string) => client.dismissAnnouncement(id),
    [client],
  );
  return { announcements, dismiss };
}

// Feature requests list + optimistic voting + create. Powers <FeatureBoard/>.
export function useFeatureRequests() {
  const client = useWherdClient();
  const revision = useWherdState().featureRequestsRevision;
  const [requests, setRequests] = useState<FeatureRequest[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    setRequests(await client.listFeatureRequests());
    setLoading(false);
  }, [client]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Refetch when a live feature-request event bumps the revision.
  const firstRev = useRef(true);
  useEffect(() => {
    if (firstRev.current) {
      firstRev.current = false;
      return;
    }
    void refresh();
  }, [revision, refresh]);

  const vote = useCallback(
    async (id: string, next: boolean) => {
      // Optimistic update, reconciled with the server's authoritative count.
      setRequests((prev) =>
        prev.map((r) =>
          r.id === id
            ? { ...r, hasVoted: next, voteCount: r.voteCount + (next ? 1 : -1) }
            : r,
        ),
      );
      const result = await client.voteFeatureRequest(id, next);
      if (result) {
        setRequests((prev) =>
          prev.map((r) =>
            r.id === id ? { ...r, hasVoted: result.voted, voteCount: result.voteCount } : r,
          ),
        );
      }
    },
    [client],
  );

  const create = useCallback(
    async (input: { title: string; body?: string }) => {
      const created = await client.createFeatureRequest(input);
      if (created) setRequests((prev) => [...prev, created]);
      return created;
    },
    [client],
  );

  return { requests, loading, refresh, vote, create };
}

// Unread changelog badge count (reactive, app-wide). Cheap to use anywhere.
export function useUnreadChangelog(): number {
  return useWherdState().changelogUnread;
}

// Effective activation: server config overridden by the Provider's `activation`
// prop (fab/shake/programmatic). Drives whether the FAB renders and shake is on.
export function useActivation(): ActivationConfig {
  const { config, localActivation } = useWherdState();
  const base = config?.activation ?? { fab: true, shake: false, programmatic: true };
  return { ...base, ...(localActivation ?? {}) };
}

// One-time "shake to open" hint. Returns visible=true the first time the app
// runs with shake enabled; call dismiss() to persist that it's been seen.
export function useShakeHint(): { visible: boolean; dismiss: () => void } {
  const client = useWherdClient();
  const { shake } = useActivation();
  const ready = useWherdState().ready;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let active = true;
    if (ready && shake) {
      void client.shouldShowShakeHint().then((show) => {
        if (active && show) setVisible(true);
      });
    }
    return () => {
      active = false;
    };
  }, [client, ready, shake]);

  const dismiss = useCallback(() => {
    setVisible(false);
    void client.dismissShakeHint();
  }, [client]);

  return { visible, dismiss };
}

// Changelog list + read tracking. Powers <WhatsNew/>.
export function useChangelog() {
  const client = useWherdClient();
  const [entries, setEntries] = useState<ChangelogEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    const data = await client.refreshChangelog();
    setEntries(data.entries);
    setLoading(false);
  }, [client]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const markRead = useCallback(
    async (id: string) => {
      setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, read: true } : e)));
      await client.markChangelogRead(id);
      await client.refreshChangelog();
    },
    [client],
  );

  const markAllRead = useCallback(async () => {
    const unread = entries.filter((e) => !e.read);
    setEntries((prev) => prev.map((e) => ({ ...e, read: true })));
    await Promise.all(unread.map((e) => client.markChangelogRead(e.id)));
    await client.refreshChangelog();
  }, [client, entries]);

  return { entries, loading, refresh, markRead, markAllRead };
}

// Resolved theme tokens (defaults ← server config ← developer prop).
// Pass the platform's current system color scheme for "system" mode.
export function useResolvedTheme(
  systemColorScheme: "light" | "dark" = "light",
): ResolvedTheme {
  const { config, localTheme } = useWherdState();
  return resolveTheme(config?.theme, localTheme, systemColorScheme);
}
